/**
 * API Client pour Dashboard - Statistiques et Graphiques
 */

import { api } from '@e-store/shared';

export interface DashboardStats {
  totalSales: number;
  salesGrowth: number;
  totalOrders: number;
  ordersGrowth: number;
  totalCustomers: number;
  customersGrowth: number;
  averageOrder: number;
  averageGrowth: number;
}

export interface ChartData {
  date: string;
  sales: number;
  count: number;
}

export interface TopProduct {
  articleId: string;
  articleName: string;
  articleReference: string;
  totalSold: number;
  quantity: number;
}

export interface VIPCustomer {
  contactId: string;
  clientName: string;
  totalSpent: number;
  orders: number;
}

export interface ClientWithDebt {
  contactId: string;
  clientName: string;
  totalDebt: number;
  unpaidInvoices: number;
  oldestDebtDate: string;
}

export interface UnpaidInvoice {
  id: string;
  invoiceNumber: string;
  type: string;
  totalTTC: number;
  totalPaid: number;
  remainingDebt: number;
  createdAt: string;
  clientName: string;
}

export interface DashboardAll {
  stats: DashboardStats;
  salesChart: ChartData[];
  topProducts: TopProduct[];
  topClients: VIPCustomer[];
  clientsWithDebts: ClientWithDebt[];
}

export const dashboardApi = {
  /**
   * Obtenir toutes les donnees du dashboard en 1 seul appel
   */
  getAll: (siteId?: string, period: '7d' | 'month' | 'year' = '7d') => {
    const params = new URLSearchParams();
    if (siteId) params.append('siteId', siteId);
    params.append('period', period);
    return api.get<DashboardAll>(`/api/dashboard/all?${params.toString()}`);
  },

  /**
   * Obtenir les statistiques principales du dashboard
   * @param siteId - Optionnel: filtrer par site
   */
  getStats: (siteId?: string) => {
    const params = new URLSearchParams();
    if (siteId) params.append('siteId', siteId);
    const queryString = params.toString();
    return api.get<DashboardStats>(`/api/dashboard/stats${queryString ? `?${queryString}` : ''}`);
  },

  /**
   * Obtenir les données du graphique des ventes
   * @param siteId - Optionnel: filtrer par site
   */
  getSalesChart: (period: '7d' | 'month' | 'year', siteId?: string) => {
    const params = new URLSearchParams();
    params.append('period', period);
    if (siteId) params.append('siteId', siteId);
    return api.get<ChartData[]>(`/api/dashboard/sales-chart?${params.toString()}`);
  },

  /**
   * Obtenir les top produits les mieux vendus
   * @param siteId - Optionnel: filtrer par site
   */
  getTopProducts: (limit: number = 5, siteId?: string) => {
    const params = new URLSearchParams();
    params.append('limit', String(limit));
    if (siteId) params.append('siteId', siteId);
    return api.get<TopProduct[]>(`/api/dashboard/top-products?${params.toString()}`);
  },

  /**
   * Obtenir les top clients VIP
   * @param siteId - Optionnel: filtrer par site
   */
  getTopClients: (limit: number = 5, siteId?: string) => {
    const params = new URLSearchParams();
    params.append('limit', String(limit));
    if (siteId) params.append('siteId', siteId);
    return api.get<VIPCustomer[]>(`/api/dashboard/top-clients?${params.toString()}`);
  },

  /**
   * Obtenir les clients avec des dettes (factures impayées)
   * @param siteId - Optionnel: filtrer par site
   */
  getClientsWithDebts: (limit: number = 10, siteId?: string) => {
    const params = new URLSearchParams();
    params.append('limit', String(limit));
    if (siteId) params.append('siteId', siteId);
    return api.get<ClientWithDebt[]>(`/api/dashboard/clients-with-debts?${params.toString()}`);
  },

  /**
   * Obtenir les factures impayées d'un client spécifique
   * @param clientId - ID du client
   * @param siteId - Optionnel: filtrer par site
   */
  getUnpaidInvoicesByClient: (clientId: string, siteId?: string) => {
    const params = new URLSearchParams();
    if (siteId) params.append('siteId', siteId);
    const queryString = params.toString();
    return api.get<UnpaidInvoice[]>(`/api/dashboard/client/${clientId}/unpaid-invoices${queryString ? `?${queryString}` : ''}`);
  },
};
